
import { useState, useCallback } from 'react';
import { CalendarEvent } from '../types';
import { toast } from 'sonner';
import { 
  startOfWeek, 
  endOfWeek, 
  differenceInDays, 
  addDays, 
  setHours, 
  setMinutes, 
  setSeconds, 
  getHours, 
  getMinutes, 
  getSeconds,
  startOfMonth,
  endOfMonth,
  differenceInCalendarDays
} from 'date-fns';
import { fromLocalISOString, toLocalISOString } from '@/utils/timezoneUtils';

export const useShiftCopy = () => {
  const [copiedShifts, setCopiedShifts] = useState<CalendarEvent[]>([]);
  const [copyMode, setCopyMode] = useState<'week' | 'custom' | 'month'>('week');
  const [withGuards, setWithGuards] = useState(false);
  const [copySourceDate, setCopySourceDate] = useState<Date | null>(null);

  const copyShifts = useCallback((shifts: CalendarEvent[], includeGuards: boolean = false, mode: 'week' | 'custom' | 'month' = 'week') => {
    if (!shifts || shifts.length === 0) {
      toast.error('No shifts to copy');
      return;
    }

    console.log(`Copying ${shifts.length} shifts with mode: ${mode}, includeGuards: ${includeGuards}`);
    
    // Store the source date from the first shift for consistent cross-week pasting
    const firstShiftDate = fromLocalISOString(shifts[0].start_date);
    setCopySourceDate(firstShiftDate);
    
    setCopiedShifts(shifts);
    setCopyMode(mode);
    setWithGuards(includeGuards);
    
    const modeText = mode === 'week' ? 'week' : mode === 'month' ? 'month' : 'custom range';
    toast.success(
      `${shifts.length} shifts copied from ${modeText}${includeGuards ? ' (with guards)' : ''}`,
      { 
        description: 'Can be pasted multiple times across any week',
        duration: 3000
      }
    );
  }, []);

  const pasteShifts = useCallback((targetDate: Date, includeGuards?: boolean) => {
    if (copiedShifts.length === 0 || !copySourceDate) {
      toast.error('No shifts to paste');
      return [];
    }

    // Use the provided includeGuards parameter, or fall back to the stored withGuards setting
    const shouldIncludeGuards = includeGuards !== undefined ? includeGuards : withGuards;

    console.log(`Pasting ${copiedShifts.length} shifts with mode: ${copyMode} to target date: ${targetDate}, including guards: ${shouldIncludeGuards}`);

    try {
      let sourceStartDate: Date;
      let targetStartDate: Date;

      if (copyMode === 'week') {
        // For week mode, use the original source week and target week
        sourceStartDate = startOfWeek(copySourceDate, { weekStartsOn: 1 });
        targetStartDate = startOfWeek(targetDate, { weekStartsOn: 1 });
      } else if (copyMode === 'month') {
        // For month mode, use the original source month and target month
        sourceStartDate = startOfMonth(copySourceDate);
        targetStartDate = startOfMonth(targetDate);
      } else {
        // For custom mode, use the exact dates but allow cross-week pasting
        sourceStartDate = copySourceDate;
        targetStartDate = targetDate;
      }

      const daysDifference = differenceInCalendarDays(targetStartDate, sourceStartDate);
      
      console.log(`Source start: ${sourceStartDate}, Target start: ${targetStartDate}, Days difference: ${daysDifference}`);

      const pastedShifts = copiedShifts.map(shift => {
        const originalStart = fromLocalISOString(shift.start_date);
        const originalEnd = fromLocalISOString(shift.end_date);
        
        // Calculate new dates by adding the difference
        const newStart = addDays(originalStart, daysDifference);
        const newEnd = addDays(originalEnd, daysDifference);
        
        console.log(`Original: ${originalStart} -> ${originalEnd}, New: ${newStart} -> ${newEnd}`);
        
        return {
          title: shift.title,
          description: shift.description || '',
          start_date: toLocalISOString(newStart),
          end_date: toLocalISOString(newEnd),
          location: shift.location || '',
          color: shift.color || 'Primary',
          assigned_guards: shouldIncludeGuards ? (shift.assigned_guards || []) : []
        };
      });

      console.log(`Generated ${pastedShifts.length} shifts for pasting`);
      return pastedShifts;
    } catch (error) {
      console.error('Error processing shifts for pasting:', error);
      toast.error('Error processing shifts for pasting');
      return [];
    }
  }, [copiedShifts, copyMode, withGuards, copySourceDate]);

  const clearCopiedShifts = useCallback(() => {
    setCopiedShifts([]);
    setCopyMode('week');
    setWithGuards(false);
    setCopySourceDate(null);
    toast.info('Copied shifts cleared');
  }, []);

  const hasCopiedShifts = copiedShifts.length > 0;

  return {
    copyShifts,
    pasteShifts,
    clearCopiedShifts,
    hasCopiedShifts,
    withGuards,
    copyMode,
    copySourceDate
  };
};
